import { useState, useEffect } from 'react';
import { Button, Dialog, DropZone, IconButton, RichEditor, TextInput } from '@origo/origo-ui';
import { XIcon } from '@phosphor-icons/react';
import type { KommunaltGalleriItem, KommunaltGalleriInput } from '../../services/kommunaltGalleriService';
import styles from './KommunaltGalleriFormDialog.module.css';

interface Props {
    open: boolean;
    item?: KommunaltGalleriItem | null;
    saving?: boolean;
    error?: string | null;
    onClose: () => void;
    onSubmit: (input: KommunaltGalleriInput) => void;
}

export function KommunaltGalleriFormDialog({ open, item, saving, error, onClose, onSubmit }: Props) {
    const [tittel, setTittel] = useState('');
    const [beskrivelse, setBeskrivelse] = useState('');
    const [bildefil, setBildefil] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [fileError, setFileError] = useState<string | null>(null);

    const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml'];

    useEffect(() => {
        if (open) {
            setTittel(item?.tittel ?? '');
            setBeskrivelse(item?.beskrivelse ?? '');
            setBildefil(null);
            setPreviewUrl(null);
            setFileError(null);
        }
    }, [open, item]);

    function handleFiles(files: File[]) {
        const file = files[0];
        if (!file) return;
        if (!ACCEPTED_TYPES.includes(file.type)) {
            setFileError('Ugyldig filformat. Kun PNG, JPG og SVG er tillatt.');
            return;
        }
        setFileError(null);
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setBildefil(file);
        setPreviewUrl(URL.createObjectURL(file));
    }

    function handleRemoveImage() {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setBildefil(null);
        setPreviewUrl(null);
        setFileError(null);
    }

    function handleClose() {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        onClose();
    }

    function handleSubmit() {
        const trimmed = tittel.trim();
        if (!trimmed) return;
        onSubmit({
            tittel: trimmed,
            beskrivelse: beskrivelse.trim() || null,
            bildefil,
        });
    }

    const isEdit = item != null;
    const canSubmit = tittel.trim().length > 0 && !saving;
    const displayUrl = previewUrl ?? item?.bilde_url ?? null;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            title={isEdit ? 'Rediger illustrasjon' : 'Legg til illustrasjon'}
            footer={
                <div className={styles.footer}>
                    <Button variant="secondary" onClick={handleClose} disabled={saving}>
                        Avbryt
                    </Button>
                    <Button onClick={handleSubmit} disabled={!canSubmit}>
                        {saving ? 'Lagrer...' : isEdit ? 'Lagre' : 'Legg til'}
                    </Button>
                </div>
            }
        >
            <div className={styles.form}>
                {error && <p className={styles.error}>{error}</p>}
                <TextInput
                    label="Tittel"
                    value={tittel}
                    onChange={(e) => setTittel(e.target.value)}
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus
                />
                <RichEditor
                    label="Beskrivelse"
                    value={beskrivelse}
                    onChange={setBeskrivelse}
                    placeholder="Valgfri beskrivelse..."
                />
                {displayUrl ? (
                    <div className={styles.imagePreviewWrapper}>
                        <p className={styles.imageLabel}>
                            {bildefil ? bildefil.name : 'Nåværende bilde'}
                        </p>
                        <div className={styles.imagePreview}>
                            <img src={displayUrl} alt={tittel || 'Forhåndsvisning'} />
                            <IconButton
                                icon={<XIcon size={14} />}
                                label="Fjern bilde"
                                variant="ghost"
                                size="sm"
                                className={styles.removeImageButton}
                                onClick={handleRemoveImage}
                            />
                        </div>
                    </div>
                ) : (
                    <>
                        <DropZone
                            label="Bilde"
                            files={[]}
                            onFiles={handleFiles}
                            onRemove={handleRemoveImage}
                            accept="image/png,image/jpeg,image/svg+xml"
                            multiple={false}
                            title="Slipp bilde her, eller klikk for å laste opp"
                            hint="SVG, PNG eller JPG"
                        />
                        {fileError && <p className={styles.error}>{fileError}</p>}
                    </>
                )}
            </div>
        </Dialog>
    );
}
